"use client"

import {
  useCallback,
  useDeferredValue,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react"
import { useRouter } from "next/navigation"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { tools, categories, getToolById, type Tool } from "@/lib/tools-registry"
import {
  getCommandPaletteServerSnapshot,
  getCommandPaletteSnapshot,
  openCommandPalette,
  setCommandPaletteOpen,
  subscribeCommandPalette,
  toggleCommandPalette,
} from "@/lib/command-palette-events"
import { useTools } from "./tools-context"
import { Star, Clock, Sparkles, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"

export function CommandPalette() {
  const open = useSyncExternalStore(
    subscribeCommandPalette,
    getCommandPaletteSnapshot,
    getCommandPaletteServerSnapshot
  )
  const [query, setQuery] = useState("")
  const deferredQuery = useDeferredValue(query)
  const router = useRouter()
  const { favorites, recent } = useTools()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleCommandPalette()
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  const handleSelect = useCallback(
    (tool: Tool) => {
      setCommandPaletteOpen(false)
      setQuery("")
      router.push(tool.href)
    },
    [router]
  )

  const normalizedQuery = deferredQuery.trim().toLowerCase()
  const filteredTools = normalizedQuery
    ? tools.filter((tool) => {
        const category = categories.find((item) => item.id === tool.category)
        const searchable = [
          tool.title,
          tool.description,
          category?.title ?? "",
          ...tool.tags,
        ]
          .join(" ")
          .toLowerCase()
        return searchable.includes(normalizedQuery)
      })
    : []

  const favoriteTools = favorites
    .map((id) => getToolById(id))
    .filter((t): t is Tool => t !== undefined)

  const recentTools = recent
    .map((id) => getToolById(id))
    .filter((t): t is Tool => t !== undefined)

  const newTools = tools.filter((t) => t.isNew).slice(0, 5)

  return (
    <CommandDialog open={open} onOpenChange={setCommandPaletteOpen}>
      <CommandInput
        placeholder="Search tools..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No tools found.</CommandEmpty>

        {/* Search Results */}
        {query && filteredTools.length > 0 && (
          <CommandGroup heading="Search Results">
            {filteredTools.map((tool) => (
              <CommandItem
                key={tool.id}
                value={tool.id}
                onSelect={() => handleSelect(tool)}
                className="flex items-center gap-3"
              >
                <tool.icon className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span>{tool.title}</span>
                    {tool.isNew && (
                      <Badge variant="secondary" className="text-xs px-1 py-0">
                        New
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {tool.description}
                  </p>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Show sections when no query */}
        {!query && (
          <>
            {/* Recent Tools */}
            {recentTools.length > 0 && (
              <>
                <CommandGroup heading="Recent">
                  {recentTools.map((tool) => (
                    <CommandItem
                      key={tool.id}
                      value={`recent-${tool.id}`}
                      onSelect={() => handleSelect(tool)}
                      className="flex items-center gap-3"
                    >
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{tool.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Favorites */}
            {favoriteTools.length > 0 && (
              <>
                <CommandGroup heading="Favorites">
                  {favoriteTools.map((tool) => (
                    <CommandItem
                      key={tool.id}
                      value={`fav-${tool.id}`}
                      onSelect={() => handleSelect(tool)}
                      className="flex items-center gap-3"
                    >
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span>{tool.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* New Tools */}
            <CommandGroup heading="New Tools">
              {newTools.map((tool) => (
                <CommandItem
                  key={tool.id}
                  value={`new-${tool.id}`}
                  onSelect={() => handleSelect(tool)}
                  className="flex items-center gap-3"
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span>{tool.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />

            {/* Categories */}
            {categories.map((category) => (
              <CommandGroup key={category.id} heading={category.title}>
                {tools.filter((t) => t.category === category.id)
                  .map((tool) => (
                    <CommandItem
                      key={tool.id}
                      value={tool.id}
                      onSelect={() => handleSelect(tool)}
                      className="flex items-center gap-3"
                    >
                      <tool.icon className="h-4 w-4 text-muted-foreground" />
                      <span>{tool.title}</span>
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}

// Search trigger button for header
export function SearchTrigger({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick ?? openCommandPalette}
      className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground rounded-md border bg-background hover:bg-accent hover:text-accent-foreground transition-colors w-full md:w-64"
    >
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left">Search tools...</span>
      <kbd className="hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
        ⌘/Ctrl K
      </kbd>
    </button>
  )
}
